import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UploadCloud, CheckCircle2, XCircle, Github } from "lucide-react";
import { toast } from "sonner";
import { http, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Card, SectionTitle, SeverityChip, Spinner } from "@/components/Kit";

const TYPES = [
  { k: "sca", label: "SCA — Dependencies", hint: "package-lock.json, requirements.txt, pom.xml, go.sum…" },
  { k: "sbom", label: "SBOM", hint: "CycloneDX or SPDX JSON" },
  { k: "iac", label: "IaC — Checkov", hint: ".tf, .yaml, Dockerfile, or .zip" },
  { k: "secrets", label: "Secrets", hint: ".patch/.diff or source files" },
];

export default function NewScan() {
  const { orgId } = useOrg();
  const navigate = useNavigate();
  const params = new URLSearchParams(useLocation().search);
  const [apps, setApps] = useState([]);
  const [appId, setAppId] = useState(params.get("app_id") || "");
  const [type, setType] = useState("sca");
  const [source, setSource] = useState("upload"); // upload | repo
  const [repoUrl, setRepoUrl] = useState("");
  const [file, setFile] = useState(null);
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const pollRef = useRef();
  const timeoutRef = useRef();

  useEffect(() => {
    if (orgId) http.get("/applications", { params: { org_id: orgId } }).then((r) => {
      setApps(r.data); setAppId((prev) => prev || r.data[0]?.id || "");
    });
  }, [orgId]);

  const selectedApp = useMemo(() => apps.find((a) => a.id === appId), [apps, appId]);
  useEffect(() => { setRepoUrl(selectedApp?.repo_url || ""); }, [selectedApp]);
  const repoValid = /github\.com[:/]+[^/\s]+\/[^/\s#?]+/i.test(repoUrl.trim());

  useEffect(() => () => { clearInterval(pollRef.current); clearTimeout(timeoutRef.current); }, []);

  const start = async () => {
    if (!appId) return;
    if (source === "upload" && !file) return;
    if (source === "repo" && !repoUrl) return;
    setBusy(true); setScan(null);
    try {
      let scanId;
      if (source === "upload") {
        const fd = new FormData();
        fd.append("org_id", orgId); fd.append("app_id", appId);
        fd.append("scan_type", type); fd.append("file", file);
        const { data } = await http.post("/scans", fd, { headers: { "Content-Type": "multipart/form-data" } });
        scanId = data.scan_id;
      } else {
        const fd = new FormData();
        fd.append("org_id", orgId); fd.append("app_id", appId);
        fd.append("scan_type", type); fd.append("repo_url", repoUrl);
        const { data } = await http.post("/scans/repo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        scanId = data.scan_id;
      }
      poll(scanId);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); setBusy(false); }
  };

  const poll = (scanId) => {
    clearInterval(pollRef.current); clearTimeout(timeoutRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await http.get(`/scans/${scanId}`, { params: { org_id: orgId } });
        setScan(data);
        if (data.status === "complete" || data.status === "failed") {
          clearInterval(pollRef.current); clearTimeout(timeoutRef.current); setBusy(false);
          if (data.status === "complete") toast.success("Scan complete");
          else toast.error("Scan failed");
        }
      } catch { /* transient poll error — keep polling */ }
    }, 2000);
    timeoutRef.current = setTimeout(() => {
      clearInterval(pollRef.current); setBusy(false);
      toast.message("Still running in the background — check the application's scan history.");
    }, 3 * 60 * 1000);
  };

  const onDrop = (e) => { e.preventDefault(); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); };

  return (
    <div className="p-6 max-w-[900px] mx-auto fade-up">
      <h1 className="font-display text-3xl font-bold text-[#A5300F]">New Scan</h1>
      <p className="text-sm text-[#8A8175] mt-1 mb-5">Upload a manifest, or scan straight from a public GitHub repository — we never store your file contents.</p>

      <Card className="p-6 space-y-5">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold">Application</label>
          <select data-testid="scan-app" value={appId} onChange={(e) => setAppId(e.target.value)}
            className="w-full mt-1 bg-white border border-[#E7D9C6] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#A5300F]">
            {apps.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold">Source</label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <button onClick={() => setSource("upload")} data-testid="source-upload"
              className={`flex items-center gap-2 text-left p-3 rounded-md border transition-colors ${source === "upload" ? "border-[#A5300F] bg-[#FFF5EB]" : "border-[#E7D9C6] hover:bg-[#FBF7F1]"}`}>
              <UploadCloud className="w-4 h-4 text-[#A5300F]" /><span className="text-sm font-semibold">Upload a file</span>
            </button>
            <button onClick={() => setSource("repo")} data-testid="source-repo"
              className={`flex items-center gap-2 text-left p-3 rounded-md border transition-colors ${source === "repo" ? "border-[#A5300F] bg-[#FFF5EB]" : "border-[#E7D9C6] hover:bg-[#FBF7F1]"}`}>
              <Github className="w-4 h-4 text-[#A5300F]" /><span className="text-sm font-semibold">Git repository</span>
            </button>
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold">Scan Type</label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {TYPES.filter((t) => source === "upload" || t.k !== "sbom").map((t) => (
              <button key={t.k} onClick={() => setType(t.k)} data-testid={`type-${t.k}`}
                className={`text-left p-3 rounded-md border transition-colors ${type === t.k ? "border-[#A5300F] bg-[#FFF5EB]" : "border-[#E7D9C6] hover:bg-[#FBF7F1]"}`}>
                <div className="text-sm font-semibold">{t.label}</div>
                <div className="text-[11px] text-[#8A8175] mt-0.5">{t.hint}</div>
              </button>
            ))}
          </div>
        </div>

        {source === "upload" ? (
          <div onDrop={onDrop} onDragOver={(e) => e.preventDefault()} onClick={() => fileRef.current.click()}
            data-testid="dropzone"
            className="border-2 border-dashed border-[#E0D2BF] rounded-lg p-8 text-center cursor-pointer hover:border-[#A5300F] transition-colors">
            <UploadCloud className="w-8 h-8 mx-auto text-[#B5AC9E]" />
            <div className="mt-2 text-sm text-[#5C554C]">{file ? <span className="font-semibold">{file.name}</span> : "Drag & drop a file, or click to browse"}</div>
            <div className="text-[11px] text-[#B5AC9E] mt-1">Max 20 MB</div>
            <input ref={fileRef} type="file" className="hidden" data-testid="file-input"
              onChange={(e) => setFile(e.target.files[0])} />
          </div>
        ) : (
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold">Public GitHub repository URL</label>
            <input data-testid="repo-url-input" value={repoUrl} onChange={(e) => setRepoUrl(e.target.value)}
              placeholder="https://github.com/owner/repo"
              className={`w-full mt-1 bg-white border rounded-md px-3 py-2 text-sm font-mono focus:outline-none ${repoUrl && !repoValid ? "border-[#A5300F]" : "border-[#E7D9C6] focus:border-[#A5300F]"}`} />
            <div className="text-[11px] mt-1" style={{ color: repoUrl && !repoValid ? "#A5300F" : "#B5AC9E" }}>
              {repoUrl && !repoValid ? "Enter a valid public GitHub URL (https://github.com/owner/repo)." : "We fetch the default branch (main/master) tarball — no upload needed. Private repos are not supported."}
            </div>
          </div>
        )}

        <button onClick={start} disabled={busy || !appId || (source === "upload" ? !file : !repoValid)} data-testid="start-scan-btn"
          className="w-full bg-[#A5300F] text-white font-semibold py-2.5 rounded-md hover:bg-[#8F2A0D] disabled:opacity-50 flex items-center justify-center gap-2">
          {busy && <Spinner />} {busy ? "Scanning…" : source === "repo" ? "Scan Repository" : "Start Scan"}
        </button>

        {scan && (
          <div className="border-t border-[#ECE6DD] pt-4" data-testid="scan-status">
            <div className="flex items-center gap-2 text-sm">
              {scan.status === "complete" ? <CheckCircle2 className="w-5 h-5 text-[#2E7D32]" />
                : scan.status === "failed" ? <XCircle className="w-5 h-5 text-[#A5300F]" />
                : <Spinner />}
              <span className="font-semibold capitalize">{scan.status}</span>
              <span className="text-[#8A8175] text-xs">· {scan.engine || "queued"} {scan.engine_version || ""}</span>
            </div>
            {scan.status === "failed" && <div className="text-xs text-[#A5300F] mt-2 font-mono">{scan.error_message}</div>}
            {scan.status === "complete" && (
              <>
                <div className="flex gap-3 mt-3">
                  {["critical", "high", "medium", "low", "info"].map((s) => (
                    <div key={s} className="text-center">
                      <div className="stat-numeral text-2xl">{scan.summary[s]}</div>
                      <div className="text-[10px] uppercase text-[#8A8175]">{s}</div>
                    </div>
                  ))}
                </div>
                <button onClick={() => navigate(`/findings?app_id=${appId}`)}
                  className="mt-4 text-sm text-[#A5300F] font-semibold underline">View findings →</button>
              </>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
